<template>
  <div
    v-tippy="{ content: tooltip }"
    class="avatar select-none"
    :class="[
      'avatar-' + color,
      rounding,
      { 'avatar-no-border': noBorder },
      { 'w-5 h-5': size == '3xs' },
      { 'w-6 h-6': size == '2xs' },
      { 'w-7 h-7': size == 'xs' },
      { 'w-8 h-8': size == 'sm' },
      { 'w-9 h-9': size == 'md' },
      { 'w-10 h-10': size == 'lg' },
      { 'w-12 h-12': size == 'xl' },
      { 'w-14 h-14': size == '2xl' },
      { 'w-16 h-16': size == '3xl' },
      { 'avatar-add-btn': addButton }
    ]"
    :aria-label="computedAriaLabel"
    role="img"
  >
    <img
      v-if="validImgUrl && !imgHasError"
      :src="url"
      :alt="imageAltText"
      :class="[{ 'p-1.5 dark:brightness-0 dark:invert my-auto': isLogo }, !isLogo && !icon ? rounding : '']"
      :width="width || undefined"
      :height="height || undefined"
      loading="lazy"
      decoding="async"
      @error="imgError(url)"
    />
    <!-- show image if url is not null, image does not have error -->
    <div
      v-else-if="text"
      class="font-semibold flex items-center justify-center h-full w-full"
      :class="[
        rounding,
        { 'text-[6px]': size == '3xs' },
        { 'text-[8px]': size == '2xs' },
        { 'text-[10px]': size == 'xs' },
        { 'text-xs': size == 'sm' },
        { 'text-md': size == 'md' },
        { 'text-md': size == 'lg' },
        { 'text-lg': size == 'xl' },
        { 'text-xl': size == '2xl' },
        { 'text-2xl': size == '3xl' }
      ]"
    >
      {{ text }}
    </div>
    <Icon
      v-else-if="icon"
      :type="icon"
      class="avatar-icon flex items-center justify-center h-full w-full"
      :class="[
        rounding,
        { 'text-[6px]': size == '3xs' },
        { 'text-[8px]': size == '2xs' },
        { 'text-[10px]': size == 'xs' },
        { 'text-sm': size == 'sm' },
        { 'text-md': size == 'md' },
        { 'text-lg': size == 'lg' },
        { 'text-xl': size == 'xl' },
        { 'text-2xl': size == '2xl' },
        { 'text-3xl': size == '3xl' }
      ]"
    ></Icon>
    <div
      v-else-if="addButton"
      class="border-primary border-2 border-dashed font-semibold text-quaternary flex items-center justify-center h-full w-full"
      :class="[
        rounding,
        { 'text-[8px]': size == '2xs' },
        { 'text-[10px]': size == 'xs' },
        { 'text-xs': size == 'sm' },
        { 'text-md': size == 'md' },
        { 'text-md': size == 'lg' },
        { 'text-lg': size == 'xl' },
        { 'text-xl': size == '2xl' },
        { 'text-2xl': size == '3xl' }
      ]"
    >
      <Icon
        type="plus"
        fa-style="fas"
      />
    </div>
    <!-- <Badge rounded>
      <div
        v-if="userPresenceUserId"
        v-tippy="{ content: userPresence.text }"
        class="avatar-user-presence"
        :class="[
          userPresence.color,
          { 'text-[10px]': size == 'xs' },
          { 'text-sm': size == 'sm' },
          { 'text-md': size == 'md' },
          { 'text-lg': size == 'lg' },
          { 'text-xl': size == 'xl' }
        ]"
      >
        <Icon :type="userPresence.icon" fa-style="fas" />
      </div>
    </Badge> -->
    <div
      v-if="statusIndicatorIcon"
      class="status-indicator"
      :class="[
        statusIndicatorColorClass,
        { 'status-indicator__3xs': size == '3xs' },
        { 'status-indicator__2xs': size == '2xs' },
        { 'status-indicator__xs': size == 'xs' },
        { 'status-indicator__sm': size == 'sm' },
        { 'status-indicator__md': size == 'md' },
        { 'status-indicator__lg': size == 'lg' },
        { 'status-indicator__xl': size == 'xl' },
        { 'status-indicator__2xl': size == '2xl' },
        { 'status-indicator__3xl': size == '3xl' }
      ]"
    >
      <Icon :type="statusIndicatorIcon" />
    </div>
  </div>
</template>

<script setup lang="ts">
import Icon from '@/components/icon/Icon.vue'

import { ref, watch, computed } from 'vue'

const props = defineProps({
  size: {
    type: String,
    default: 'md'
  },
  url: {
    type: String,
    default: null
  },
  badgeContent: {
    type: String,
    default: null
  },
  text: {
    type: String,
    default: null
  },
  tooltip: {
    type: String,
    default: null
  },
  icon: {
    type: String,
    default: null
  },
  color: {
    type: String,
    default: 'light'
  },
  rounding: {
    type: String,
    default: 'rounded-full'
  },
  statusIndicatorIcon: {
    type: String,
    default: null
  },
  statusIndicatorColorClass: {
    type: String,
    default: null
  },
  isLogo: {
    type: Boolean,
    default: false
  },
  addButton: {
    type: Boolean,
    default: false
  },
  noBorder: {
    type: Boolean,
    default: false
  },
  alt: {
    type: String,
    default: null
  },
  width: {
    type: [Number, String],
    default: null
  },
  height: {
    type: [Number, String],
    default: null
  }
})

const validImgUrl = ref(false)
const imgHasError = ref(false)

function validateImgUrl(imgUrl: string) {
  const validImgTypes = ['.jpg', '.jpeg', '.png', '.svg']

  if (imgUrl != null && validImgTypes.find(type => imgUrl.includes(type))) {
    validImgUrl.value = true
  } else {
    validImgUrl.value = false
  }
}

function imgError(imgUrl: string) {
  imgHasError.value = true
  console.error('Failed to load image with url: ' + imgUrl)
}

validateImgUrl(props.url)
// Check img url when props change (Mostly for dropdowns where the avatar image gets updated)
watch(props, () => {
  validImgUrl.value = false
  imgHasError.value = false
  validateImgUrl(props.url)
})

// Computed property for image alt text
const imageAltText = computed(() => {
  // If there's an explicit alt prop, use it
  if (props.alt) {
    return props.alt
  }

  // If it's a logo
  if (props.isLogo) {
    return 'Logo image'
  }

  // If there's text content, use it for context
  if (props.text) {
    return `Avatar image for ${props.text}`
  }

  // Default for avatar images
  return 'Avatar image'
})

// Computed property for aria-label
const computedAriaLabel = computed(() => {
  // If there's an explicit alt prop, use it
  if (props.alt) {
    return props.alt
  }

  if (props.text) {
    return `Avatar for ${props.text}`
  }

  if (props.icon) {
    return `Avatar with icon ${props.icon}`
  }

  if (props.addButton) {
    return 'Add avatar button'
  }

  if (props.statusIndicatorIcon) {
    return `Avatar with status indicator ${props.statusIndicatorIcon}`
  }

  return 'Avatar'
})
</script>

<style>
.avatar {
  position: relative;
  flex-shrink: 0;
  display: flex;
  background-color: var(--bg-tertiary);
  color: var(--text-secondary);
  box-sizing: content-box;
}
.avatar:hover {
  z-index: 1;
}

.avatar-icon {
  transition: all 0.3s ease-in-out;
  width: 100%;
  display: flex;
}

.avatar.avatar-add-btn {
  background-color: transparent;
}
.avatar.avatar-add-btn:hover {
  background-color: var(--utility-gray-50);
  cursor: pointer;
}

.avatar-brand {
  background-color: var(--utility-brand-50);
  color: var(--text-brand);
}

.avatar-success {
  background-color: var(--utility-success-50);
  color: var(--text-success);
}

.avatar-warning {
  background-color: var(--utility-warning-50);
  color: var(--text-warning);
}

.avatar-danger {
  background-color: var(--utility-error-50);
  color: var(--text-error);
}

.avatar-info {
  background-color: var(--utility-purple-50);
  color: var(--text-info);
}

.status-indicator {
  z-index: 1;
  position: absolute;
  bottom: -1px;
  right: -1px;
  border-radius: 100%;
  display: flex;
  justify-content: center;
  padding-top: 2px;
  align-items: center;
  outline: 2px solid var(--bg-primary);
  color: white;
}
.status-indicator__3xs {
  height: 6px;
  width: 6px;
  font-size: 0.3rem;
  outline-width: 1px;
}
.status-indicator__2xs {
  height: 8px;
  width: 8px;
  font-size: 0.35rem;
  outline-width: 1.5px;
}
.status-indicator__xs {
  height: 10px;
  width: 10px;
  font-size: 0.4rem;
  outline-width: 1.5px;
}
.status-indicator__sm {
  height: 12px;
  width: 12px;
  font-size: 0.45rem;
  outline-width: 2px;
}
.status-indicator__md {
  height: 12px;
  width: 12px;
  font-size: 0.45rem;
}
.status-indicator__lg {
  height: 14px;
  width: 14px;
  font-size: 0.45rem;
}
.status-indicator__xl {
  height: 16px;
  width: 16px;
  font-size: 0.6rem;
}
.status-indicator__2xl {
  height: 18px;
  width: 18px;
  font-size: 0.6rem;
}
.status-indicator__3xl {
  height: 20px;
  width: 20px;
  font-size: 0.7rem;
}

.avatar-presence-status-offline {
  background-color: var(--bg-tertiary);
}
.avatar-presence-status-online {
  background-color: var(--success);
}
.avatar-presence-status-away {
  background-color: var(--warning);
}
</style>
